import { useEffect, useRef } from 'react';
import { KeyValue } from '@mews/enums';
import {
    ActivableRendererProps,
    ANIMATION_DURATION,
    ElementOrElementGetter,
    getTooltipPositionFromPlacement,
    handleRefSplit,
    Positioner,
    PositionStrict,
    ScrollAwayProps,
    Stack,
    useClickAway,
    withScrollAway,
} from '@optimus-web/core';
import { useKeyboardInteraction, useVisibleWithDelay } from './hooks';
import {
    Arrow,
    PopoverElement,
    PopoverHeaderElement,
    PopoverSubtitle,
    PopoverTitle,
    StyledOpenableOverlay,
} from './style';

const POPOVER_MAX_WIDTH = 444;
const POPOVER_MIN_WIDTH = 200;

interface PopoverBaseProps extends ActivableRendererProps {
    active: boolean;
    anchorElement: ElementOrElementGetter;
    children: React.ReactNode;
    onClose: () => void;
    title?: String | React.ReactNode;
    subtitle?: String | React.ReactNode;
    position?: PositionStrict;
    headerActionSlot?: React.ReactNode;
}
export type PopoverProps = PopoverBaseProps & ScrollAwayProps;

const PopoverComponent = (props: PopoverProps) => {
    const {
        children,
        title,
        subtitle,
        anchorElement,
        headerActionSlot,
        position,
        registerScrollAway,
        deregisterScrollAway,
        onClose,
        active,
    } = props;

    const popoverElementRef = useRef<HTMLDivElement>(null);
    const positionerRef = useRef<Positioner>(null);

    const { registerClickAway, deregisterClickAway } = useClickAway();

    useEffect(() => {
        if (active) {
            const roots = [() => popoverElementRef.current];

            registerClickAway({ roots, handler: onClose });
            registerScrollAway({ roots, handler: onClose });
        } else {
            deregisterClickAway();
            deregisterScrollAway();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(
        () => () => {
            deregisterScrollAway();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useKeyboardInteraction({
        active,
        onClose,
        deactivationKeys: [KeyValue.Escape],
    });

    const isVisible = useVisibleWithDelay(active, ANIMATION_DURATION.default);

    const popoverPosition = positionerRef.current
        ? getTooltipPositionFromPlacement(positionerRef.current.getCurrentPlacement())
        : position;

    const positionerProps = {
        opened: isVisible,
        anchorElement,
        ref: handleRefSplit({ ownRef: positionerRef }),
        placement: popoverPosition,
        maxWidth: POPOVER_MAX_WIDTH,
        minWidth: POPOVER_MIN_WIDTH,
    };

    const RenderTitle = title ? (
        <PopoverTitle ellipsis maxLines={3}>
            {title}
        </PopoverTitle>
    ) : null;
    const RenderSubtitle = subtitle ? <PopoverSubtitle>{subtitle}</PopoverSubtitle> : null;

    if (!active) return null;

    return (
        <StyledOpenableOverlay {...positionerProps}>
            <PopoverElement ref={popoverElementRef}>
                {title || subtitle || headerActionSlot ? (
                    <PopoverHeaderElement distribution="space-between">
                        {title || subtitle ? (
                            <Stack vertical>
                                {RenderTitle}
                                {RenderSubtitle}
                            </Stack>
                        ) : null}
                        {headerActionSlot}
                    </PopoverHeaderElement>
                ) : null}
                {children}
                <Arrow data-popper-arrow />
            </PopoverElement>
        </StyledOpenableOverlay>
    );
};

export const Popover = withScrollAway(PopoverComponent);
